import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { Box, Container, Grid, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createCompanyDepartmentFormFields } from '../../../forms/fields/formFields/createCompanyDepartment';
import { createCompanyDepartmentSchema } from '../../../forms/validationSchema/formSchema/createCompanyDepartment';
import { useAppDispatch } from '../../../redux/hooks';
import {
  createCompanyDepartment,
  fetchCompanyDepartment,
  updateCompanyDepartment,
} from '../../../redux/modules/b2bThriftPlan/departments/companyDepartments.actions';
import {
  companyDepartmentCreateLoadingSelector,
  companyDepartmentLoadingSelector,
  companyDepartmentUpdateLoadingSelector,
  companyDepartmentsSelector,
} from '../../../redux/modules/b2bThriftPlan/departments/companyDepartments.selectors';

export const CreateCompanyDepartment = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { companyId, action } = useParams<{ companyId: string; action: string }>();
  const isCreateLoading = useSelector(companyDepartmentCreateLoadingSelector);
  const isUpdateLoading = useSelector(companyDepartmentUpdateLoadingSelector);
  const isFetchLoading = useSelector(companyDepartmentLoadingSelector);
  const { selectedCompanyDepartment } = useSelector(companyDepartmentsSelector);
  const isEdit = action !== 'create';
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});

  const { handleSubmit, values, handleChange, touched, errors, resetForm, isValid, dirty } = useFormik({
    initialValues: {
      [createCompanyDepartmentFormFields.name]: '',
      [createCompanyDepartmentFormFields.description]: '',
    },
    validationSchema: createCompanyDepartmentSchema,
    validateOnMount: true,
    onSubmit: async (formValues) => {
      if (!companyId) {
        enqueueSnackbar('Company ID is required', { variant: 'error' });
        return;
      }

      try {
        if (isEdit && action) {
          // Get only changed fields
          const changedFields: Record<string, any> = {};
          Object.keys(formValues).forEach((key) => {
            const currentValue = formValues[key];
            const initialValue = initialValues[key];
            // Normalize values for comparison (handle empty strings and null/undefined)
            const normalizedCurrent = currentValue === '' ? null : currentValue;
            const normalizedInitial = initialValue === '' ? null : initialValue;
            if (normalizedCurrent !== normalizedInitial) {
              changedFields[key] = currentValue;
            }
          });

          await dispatch(
            updateCompanyDepartment({
              companyId: Number(companyId),
              departmentId: Number(action),
              payload: changedFields as any,
            }),
          ).unwrap();
          enqueueSnackbar('Company department updated successfully!', { variant: 'success' });
        } else {
          await dispatch(
            createCompanyDepartment({
              companyId: Number(companyId),
              payload: {
                name: formValues[createCompanyDepartmentFormFields.name],
                description: formValues[createCompanyDepartmentFormFields.description],
              },
            }),
          ).unwrap();
          enqueueSnackbar('Company department created successfully!', { variant: 'success' });
        }
        navigate(`/companies/${companyId}/departments`);
      } catch (error: any) {
        const errorMessage =
          error?.message || (isEdit ? 'Company department update error!' : 'Company department creation error!');
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
  });

  useEffect(() => {
    if (isEdit && action && companyId) {
      dispatch(fetchCompanyDepartment({ companyId: Number(companyId), departmentId: Number(action) }));
    }
  }, [isEdit, action, companyId, dispatch]);

  useEffect(() => {
    if (isEdit && selectedCompanyDepartment) {
      const initialFormValues = {
        [createCompanyDepartmentFormFields.name]: selectedCompanyDepartment.name || '',
        [createCompanyDepartmentFormFields.description]: selectedCompanyDepartment.description || '',
      };
      setInitialValues(initialFormValues);
      resetForm({
        values: initialFormValues,
      });
    } else {
      setInitialValues({});
    }
  }, [isEdit, selectedCompanyDepartment, resetForm]);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">{isEdit ? 'Edit Company Department' : 'Create Company Department'}</Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="name"
              name={createCompanyDepartmentFormFields.name}
              label="Name *"
              value={values.name}
              onChange={handleChange}
              error={touched.name && !!errors.name}
              helperText={touched.name && errors.name}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="description"
              name={createCompanyDepartmentFormFields.description}
              label="Description *"
              multiline
              rows={4}
              value={values.description}
              onChange={handleChange}
              error={touched.description && !!errors.description}
              helperText={touched.description && errors.description}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={isCreateLoading || isUpdateLoading || isFetchLoading}
                startIcon={<SaveIcon />}
                disabled={!isValid || !dirty || isCreateLoading || isUpdateLoading || isFetchLoading}
              >
                {isEdit ? 'Update Department' : 'Create Department'}
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};
